<script setup>
import { computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { format } from 'date-fns';

import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import ButtonV4 from 'next/button/Button.vue';

const { currentAccount } = useAccount();
const {
  captainEnabled,
  captainLimits,
  documentLimits,
  responseLimits,
  fetchLimits,
} = useCaptain();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();
const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return customAttributes.value.plan_name;
});

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    store.dispatch('accounts/subscription');
    fetchLimits();
  }
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

onMounted(fetchAccountDetails);
</script>

<template>
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-credit-card"
        accent="green"
        :title="$t('BILLING_SETTINGS.TITLE')"
        :subtitle="$t('BILLING_SETTINGS.DESCRIPTION')"
      />
    </template>
    <woot-loading-state
      v-if="uiFlags.isFetchingItem"
      :message="$t('ATTRIBUTES_MGMT.LOADING')"
    />
    <p
      v-else-if="!hasABillingPlan"
      class="flex flex-col items-center justify-center h-full text-base text-n-slate-12 p-8"
    >
      {{ $t('BILLING_SETTINGS.NO_BILLING_USER') }}
    </p>
    <section v-else class="grid gap-4">
      <BillingCard
        :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
        :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
      >
        <template #action>
          <ButtonV4 sm solid blue @click="onClickBillingPortal">
            {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
          </ButtonV4>
        </template>
        <div
          v-if="planName || subscribedQuantity || subscriptionRenewsOn"
          class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
        >
          <DetailItem
            :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
            :value="planName"
          />
          <DetailItem
            v-if="subscribedQuantity"
            :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
            :value="subscribedQuantity"
          />
          <DetailItem
            v-if="subscriptionRenewsOn"
            :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
            :value="subscriptionRenewsOn"
          />
        </div>
      </BillingCard>
      <BillingCard
        v-if="captainEnabled"
        :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
        :description="$t('BILLING_SETTINGS.CAPTAIN.DESCRIPTION')"
      >
        <template #action>
          <ButtonV4 sm faded slate disabled>
            {{ $t('BILLING_SETTINGS.CAPTAIN.BUTTON_TXT') }}
          </ButtonV4>
        </template>
        <div v-if="captainLimits && responseLimits" class="px-5">
          <BillingMeter
            :title="$t('BILLING_SETTINGS.CAPTAIN.RESPONSES')"
            v-bind="responseLimits"
          />
        </div>
        <div v-if="captainLimits && documentLimits" class="px-5">
          <BillingMeter
            :title="$t('BILLING_SETTINGS.CAPTAIN.DOCUMENTS')"
            v-bind="documentLimits"
          />
        </div>
      </BillingCard>
      <BillingCard
        v-else
        :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
        :description="$t('BILLING_SETTINGS.CAPTAIN.UPGRADE')"
      >
        <template #action>
          <ButtonV4 sm solid slate @click="onClickBillingPortal">
            {{ $t('CAPTAIN.PAYWALL.UPGRADE_NOW') }}
          </ButtonV4>
        </template>
      </BillingCard>

      <BillingHeader
        class="px-1 mt-5"
        :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
        :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
      >
        <ButtonV4
          sm
          solid
          slate
          icon="i-lucide-life-buoy"
          @open="onToggleChatWindow"
        >
          {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
        </ButtonV4>
      </BillingHeader>
    </section>
  </PageLayout>
</template>
